import { useEffect, useMemo, useRef, useState } from 'react';
import i18next from 'i18next';
import { resolveResultsCountLabel } from '../BKJComboBoxAsync.utils.js';
export function useBKJComboBoxAsyncContextHooks() {
    const [anchorElement, setAnchorElement] = useState();
    const [dropdownElement, setDropdownElement] = useState();
    const values = useMemo(() => ({ anchorElement, dropdownElement }), [anchorElement, dropdownElement]);
    const setMethods = useMemo(() => ({ setAnchorElement, setDropdownElement }), []);
    return [values, setMethods];
}
const LOADING_ANNOUNCEMENT_DELAY_MS = 1000;
const DEFAULT_LOADING_LABEL = 'Loading results…';
export function useLoadingAnnouncer({ isLoading, isOpen, optionsCount, labels, }) {
    const [liveMessage, setLiveMessage] = useState('');
    const timerRef = useRef(null);
    const firedRef = useRef(false);
    const isOpenRef = useRef(isOpen);
    const prevIsLoadingRef = useRef(isLoading);
    // Keep isOpenRef synchronised so the timer callback can read the latest
    // value without stale-closure bugs (per R5).
    useEffect(() => {
        isOpenRef.current = isOpen;
    }, [isOpen]);
    // When the dropdown closes, cancel any pending announcement (FR-006).
    useEffect(() => {
        if (!isOpen) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            firedRef.current = false;
            setLiveMessage('');
        }
    }, [isOpen]);
    // Observe isLoading transitions. Rising edge arms the 1s timer; falling
    // edge clears the timer and (when dropdown still open) writes the
    // results-count message for non-empty resolves.
    useEffect(() => {
        const prev = prevIsLoadingRef.current;
        prevIsLoadingRef.current = isLoading;
        if (!prev && isLoading) {
            // Rising edge: start the 1s timer (per R5).
            if (!timerRef.current && !firedRef.current) {
                timerRef.current = setTimeout(() => {
                    timerRef.current = null;
                    if (!isOpenRef.current)
                        return; // Closed during fetch — suppress (FR-006).
                    if (firedRef.current)
                        return;
                    firedRef.current = true;
                    setLiveMessage(labels.loadingLabel ?? DEFAULT_LOADING_LABEL);
                }, LOADING_ANNOUNCEMENT_DELAY_MS);
            }
            return;
        }
        if (prev && !isLoading) {
            // Falling edge: clear timer, reset fired-flag, announce results.
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            firedRef.current = false;
            if (!isOpenRef.current) {
                setLiveMessage('');
                return;
            }
            if (optionsCount > 0) {
                // Non-empty resolve: announce results count on EVERY resolve
                // (no cache-hit suppression, per Q4 / I-A04).
                setLiveMessage(resolveResultsCountLabel(labels.resultsCountLabel, optionsCount));
            }
            else {
                // Empty resolve: clear any stale loading message. No live-region
                // "No results" announcement — the in-listbox element conveys it
                // (per Q2 / I-A05).
                setLiveMessage('');
            }
        }
    }, [isLoading, optionsCount, labels.loadingLabel, labels.resultsCountLabel]);
    // Cleanup on unmount.
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);
    // WEB-4815 / FR-002 / FR-006 / I-A08: aria-busy is meaningful only while
    // the listbox is open (the only time AT can observe it). Gating isBusy
    // on isOpen also ensures no stuck aria-busy="true" after dropdown close
    // during a still-in-flight fetch.
    return { liveMessage, isBusy: isLoading && isOpen };
}
/**
 * WEB-4815 / R1: exposes the i18next-based fallback for callers that need
 * to resolve the NoResults text when the consumer has not supplied
 * `noResultsLabel`. Kept here so the i18next import lives next to its only
 * remaining consumer and the dependency can be removed with a single file
 * change in the future cleanup ticket.
 */
export function resolveNoResultsLabel(noResultsLabel) {
    if (typeof noResultsLabel === 'string' && noResultsLabel.length > 0)
        return noResultsLabel;
    return i18next.t('translations:NO_RESULTS');
}
//# sourceMappingURL=BKJComboBoxAsync.context.hooks.js.map